/**
 * Service Ops status catalogue — client side of the single source of truth
 * (`backend/services/service_status_catalogue.py`, served by
 * `GET /api/service-ops/status-catalogue`).
 *
 * The modal dropdown, the page filter and the KPI cards each used to hardcode
 * their own list. They drifted until the modal could set a status that no
 * filter could find, so a cancelled job simply vanished from the page.
 */
import { useEffect, useState } from 'react';
import { serviceOpsApi } from '@/services/api';

// Badge classes keyed by the catalogue's `tone` — a status's colour is
// declared once on the server and renders identically everywhere.
export const TONE_BADGE = {
  emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  teal:    'bg-teal-100 text-teal-700 border-teal-200',
  cyan:    'bg-cyan-100 text-cyan-700 border-cyan-200',
  indigo:  'bg-indigo-100 text-indigo-700 border-indigo-200',
  green:   'bg-green-200 text-green-800 border-green-300',
  amber:   'bg-amber-100 text-amber-800 border-amber-300',
  rose:    'bg-rose-100 text-rose-700 border-rose-200',
  orange:  'bg-orange-100 text-orange-700 border-orange-200',
  slate:   'bg-slate-100 text-slate-700 border-slate-200',
};

export const TONE_SOLID = {
  emerald: 'bg-emerald-500', teal: 'bg-teal-500', cyan: 'bg-cyan-500',
  indigo: 'bg-indigo-500', green: 'bg-green-500', amber: 'bg-amber-500',
  rose: 'bg-rose-500', orange: 'bg-orange-500', slate: 'bg-slate-500',
};

let cache = null;
let inflight = null;

export function useStatusCatalogue() {
  const [catalogue, setCatalogue] = useState(cache);
  useEffect(() => {
    if (cache) return undefined;
    if (!inflight) {
      inflight = serviceOpsApi.statusCatalogue()
        .then((r) => { cache = r.data; return cache; })
        .catch((e) => { inflight = null; throw e; });
    }
    let alive = true;
    inflight.then((d) => { if (alive) setCatalogue(d); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  return catalogue;
}

export const statusMeta = (cat, value) =>
  (cat?.statuses || []).find((s) => s.value === value) || {};
export const statusLabel = (cat, value) => statusMeta(cat, value).short || value || '—';
export const statusBadge = (cat, value) =>
  TONE_BADGE[statusMeta(cat, value).tone] || TONE_BADGE.slate;
export const reasonsFor = (cat, value) => (cat?.reasons || {})[value] || [];
export const requiresReason = (cat, value) => !!statusMeta(cat, value).requires_reason;
export const statusesIn = (cat, group) =>
  (cat?.statuses || []).filter((s) => s.group === group);
